// src/runtime/ContentStrategyRuntime/types.ts
// ============================================
// Content Strategy Runtime 类型定义
//
// 依据《XEAOS 企业操作系统白皮书》第十四章《Content Protocol（内容协议）》：
//   Content Strategy Runtime 生成 Platform Brief（策略简报），包含：
//   平台人设、受众画像、内容结构、禁忌、钩子与 CTA。
//   它回答"这个平台、这个品牌、这个目标下，应该生产什么样的产品"。
//
// 生成流程强制（必须严格按顺序执行）：
//   ① 选择品牌 → ② 选择平台 → ③ 选择内容目标 → ④ 知识检索 → ⑤ 策略简报 → ⑥ 素材生成 → ⑦ 发布
// ============================================

// ============================================
// 内容目标
// ============================================

export type ContentGoal =
  | 'lead_gen' // 获客
  | 'brand' // 品牌
  | 'conversion' // 转化
  | 'education' // 教育
  | 'recruitment' // 招聘
  | 'seo' // SEO
  | 'geo'; // GEO

export const ALL_CONTENT_GOALS: Array<{ value: ContentGoal; label: string }> = [
  { value: 'lead_gen', label: '获客' },
  { value: 'brand', label: '品牌' },
  { value: 'conversion', label: '转化' },
  { value: 'education', label: '教育' },
  { value: 'recruitment', label: '招聘' },
  { value: 'seo', label: 'SEO' },
  { value: 'geo', label: 'GEO' },
];

export const CONTENT_GOAL_LABEL: Record<ContentGoal, string> = {
  lead_gen: '获客',
  brand: '品牌',
  conversion: '转化',
  education: '教育',
  recruitment: '招聘',
  seo: 'SEO',
  geo: 'GEO',
};

// ============================================
// 品牌身份
// ============================================

export interface BrandIdentity {
  /** 品牌 ID（短名） */
  id: string;
  /** 品牌中文名 */
  name: string;
  /** 一句话定位 */
  tagline: string;
  /** 核心产品 */
  product: string;
  /** 目标客户 */
  targetCustomer: string;
  /** 价值主张 */
  valueProp: string;
  /** 核心关键词（GEO/SEO 植入用） */
  keywords: string[];
}

// ============================================
// 平台简报（Platform Brief / 策略简报）
// ============================================

export interface PlatformBrief {
  /** 品牌 */
  brand: BrandIdentity;
  /** 平台 ID（douyin_enterprise / douyin_personal / xiaohongshu / shipinhao / wechat / zhihu / bilibili） */
  platform: string;
  /** 平台中文名 */
  platformName: string;
  /** 内容目标 */
  goal: ContentGoal;
  /** 内容目标中文名 */
  goalLabel: string;
  /** 平台人设（该平台下品牌"是谁"） */
  persona: string;
  /** 受众画像 */
  audience: string;
  /** 内容结构（来自 Platform Spec） */
  structure: string;
  /** 禁忌（来自 Platform Spec） */
  taboos: string[];
  /** 钩子策略 */
  hook: string;
  /** CTA 策略 */
  cta: string;
  /** 语气/风格（来自 Platform Spec） */
  tone: string;
  /** 时长/字数约束（来自 Platform Spec） */
  duration: string;
  /** 知识检索引用（Rule 04 可追溯） */
  knowledgeRefs: Array<{ title: string; path?: string }>;
  /** 生成提示词约束（供 Platform Adapter 使用） */
  constraints: string[];
  /** 生成时间 */
  generatedAt: string;
  /** 今日经验素材（经验驱动选题；无则缺省） */
  experience?: {
    topic: string;
    material: string;
    source: string;
  };
}


// ============================================
// 查询 / 命令枚举
// ============================================

export enum ContentStrategyQuery {
  /** 列出可用品牌 */
  ListBrands = 'list_brands',
  /** 获取品牌详情 */
  GetBrand = 'get_brand',
  /** 列出平台 */
  ListPlatforms = 'list_platforms',
  /** 获取平台规格（Platform Spec） */
  GetPlatformSpec = 'get_platform_spec',
  /** 获取今日经验（经验驱动选题） */
  GetDailyExperience = 'get_daily_experience',
}

export enum ContentStrategyCommand {
  /** 生成策略简报（必须传入 brand + platform + goal，内部强制知识检索） */
  GenerateBrief = 'generate_brief',
  /** 生成完整策略简报并写入 Vault（XEAOS/Daily/Brief-{date}.md） */
  GenerateBriefAndSave = 'generate_brief_and_save',
  /** 生成今日经验笔记并落盘（10_Operations/Daily/经验-{date}.md） */
  PersistDailyExperience = 'persist_daily_experience',
}

// ============================================
// 统一响应类型
// ============================================

export interface ContentStrategyResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
}
